/**
 * @module PersistedState
 *
 * Crash-proof reads of the JSON values Vuex modules cache in localStorage.
 *
 * **This module must never import anything**, for the same reason `@/i18n/LanguageTypes` must not:
 * its callers are Vuex module property initializers, which run during app bootstrap while much of
 * the module graph is still half-evaluated. Anything imported here could be read before it is
 * initialized, and a throw in a property initializer takes the whole bootstrap down. The page then
 * renders blank with no route back, because the bad value is re-read on every subsequent start.
 *
 * That is not hypothetical. `JSON.stringify(undefined)` returns `undefined`, which `setItem`
 * stores as the literal text `"undefined"`, and `JSON.parse("undefined")` throws. The guards those
 * initializers used (`localStorage.getItem(key) || "false"`) only substitute a default for `null`
 * and the empty string, so the poisoned text sails straight through into `JSON.parse`. It happened
 * once already with the `locale` key and blanked the app until storage was cleared by hand.
 *
 * Reads here therefore never throw, and they **repair** storage: a value that cannot be used is
 * deleted, so a browser already holding one recovers on its next load rather than staying broken.
 */

/** Narrows an unparsed value to the shape a caller is willing to accept. */
type Validator<T> = (value: unknown) => value is T;

/** Accepts a JSON boolean. */
export function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

/** Accepts any JSON array, without judging its elements. */
export function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/** Accepts a JSON array whose elements are all strings. */
export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

/**
 * Parses one persisted value. Returns `null` when it is absent or unusable, which the caller
 * answers with its own default.
 *
 * Exported separately from {@link readPersistedJson} so the parsing rules can be tested without a
 * storage backend.
 */
export function parsePersistedJson<T>(raw: string | null, isValid: Validator<T>): T | null {
  if (raw === null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    // Not JSON at all, which includes the literal text "undefined".
    return null;
  }

  return isValid(parsed) ? parsed : null;
}

/**
 * Reads one persisted value, dropping it from storage when it cannot be used.
 *
 * @param key - the localStorage key.
 * @param isValid - what the caller accepts; anything else is treated as absent.
 * @returns the stored value, or `null` when there is nothing usable to return.
 */
export function readPersistedJson<T>(
  key: string,
  isValid: Validator<T>,
  storage: () => Storage = () => localStorage
): T | null {
  let raw: string | null;
  try {
    raw = storage().getItem(key);
  } catch (error) {
    // Storage can be unavailable outright (private modes, blocked site data). Reading it must not
    // be the reason the app fails to start, so fall back to the caller's default.
    return null;
  }

  const value = parsePersistedJson(raw, isValid);

  if (value === null && raw !== null) {
    try {
      storage().removeItem(key);
    } catch (error) {
      // Repairing storage is best effort; failing to delete is not worth a broken start.
    }
  }

  return value;
}

/**
 * Reads a persisted array, trusting its elements.
 *
 * The elements are **not** validated against `T`. That matches what these caches always did, and
 * the guard that matters is the one above: an array is an array, so nothing downstream throws
 * while iterating it. Use {@link readPersistedJson} with a stricter validator where the element
 * shape actually has to hold.
 */
export function readPersistedArray<T>(key: string): T[] | null {
  return readPersistedJson(key, isArray) as T[] | null;
}

/**
 * The tab's own storage, for state that belongs to the tab's Firebase session (which IRES Office keeps
 * per tab): a second tab signed in to another client, "Als Mandant anmelden" (#62), must not overwrite
 * it. Survives a reload of the tab, not the tab.
 */
export const tabStorage = (): Storage => sessionStorage;

/** Writes a value as JSON, best effort: storage can be unavailable or full. */
export function writePersistedJson(key: string, value: unknown, storage: () => Storage = () => localStorage): void {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch (error) {
    // Not worth failing the action that changed the value; the next reload falls back to the default.
  }
}

/** Removes a value, best effort. */
export function removePersisted(key: string, storage: () => Storage = () => localStorage): void {
  try {
    storage().removeItem(key);
  } catch (error) {
    // As above: nothing to recover.
  }
}
